import Stepper from "@/components/Stepper";
import React, {FC, useState, useEffect} from "react";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Send, CheckCircle, AlertCircle, Sparkles, Brain, Wand2} from "lucide-react";
import Card from "@/components/Card";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import DashboardLayout from "@/components/layouts/Dashboard";
import {
  apiValidateDescription,
  apiGenerateProjectSpecs,
  ProjectDescriptionValidationResponse, ProjectDetailResponse
} from "@/services/ProjectService"
import Ckeditor5Field from "@/components/Ckeditor5Field.tsx";
import {Form, Formik, FormikProps} from "formik";
import {FormContainer, FormItem} from "@/components/ui/Form/index";
import * as Yup from "yup";
import AIButton from "@/components/ui/AIButton";
import { usePendingProject } from "@/contexts/PendingProjectProvider";

type CreateProjectFormSchema = {
  projectDescription: string
}

const Generate: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  let {id} = useParams();
  const formikRef = React.useRef<FormikProps<CreateProjectFormSchema>>(null);
  const { clearPendingProject } = usePendingProject();

  const validationSchema = Yup.object().shape({
    projectDescription: Yup.string()
        .test(
            'is-html-empty',
            'Please enter the project description',
            value => {
              const strippedValue = value!.replace(/<[^>]*>?/gm, '').trim();
              return strippedValue.length > 0;
            }
        )
        .required('Please enter the project description'),
  })

  const [validationError, setValidationError] = useState<string>('');
  const [improvedPrompt, setImprovedPrompt] = useState<string>('');
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [autoSubmitted, setAutoSubmitted] = useState(false);

  // Enhanced function to clean and format API response text
  const formatApiResponse = (text: string) => {
    return text
        .replace(/\\n/g, '\n')  // Replace \n with actual line breaks
        .replace(/\n{3,}/g, '\n\n')  // Replace 3+ consecutive line breaks with double
        .replace(/\n\s*\n/g, '\n')  // Replace line breaks with only whitespace between them
        .replace(/^\s+|\s+$/gm, '')  // Trim whitespace from start and end of each line
        .trim();
  };

  const validatePrompt = async (prompt: string) => {
    setValidating(true)  // Start validation
    setValidationError('')
    setImprovedPrompt('')
    setError('')

    try {
      const response = await apiValidateDescription<ProjectDescriptionValidationResponse>(prompt)
      if (response.data) {
        if (!response.data.validated) {
          setValidationError(formatApiResponse(response.data.validationText));
          setImprovedPrompt(response.data.improvedPrompt);
        }

        setValidating(false);
        return response.data.validated
      }
    } catch (errors: any) {
      setError(errors?.response?.data?.message || errors.toString());
    }

    setValidating(false);
    return false
  };


  const handleUsePrompt = () => {
    if (formikRef.current && improvedPrompt) {
      formikRef.current.setFieldValue('projectDescription', improvedPrompt);
    }
  };

  const onDescriptionValidation = (
      values: CreateProjectFormSchema,
      setSubmitting: (isSubmitting: boolean) => void
  ) => {
    const {projectDescription} = values

    setSubmitting(true);
    setError('');

    validatePrompt(projectDescription).then((isValid) => {
      if (isValid) {
        if (id !== undefined) {
          apiGenerateProjectSpecs<ProjectDetailResponse>(id, projectDescription).then((response) => {
            if (response.data) {
              setSubmitting(false);
              navigate(`/architecture/preview/${id}`);
            }
          }).catch((errors: any) => {
            setError(errors?.response?.data?.message || errors.toString());
          })
        } else {
          setError("The project id is not defined")
        }
      }
    }).catch(() => {
      setError("An error occurred while validating the prompt")
    })

    setSubmitting(false)
  }

  // Auto-submit prompt when coming from home page
  useEffect(() => {
    const state = location.state as { autoSubmitPrompt?: string; fromHomePage?: boolean } | null;

    if (state?.autoSubmitPrompt && state?.fromHomePage && !autoSubmitted && formikRef.current) {
      console.log('[Generate] Auto-submitting prompt from home page:', state.autoSubmitPrompt);
      setAutoSubmitted(true);

      // Set the prompt value
      formikRef.current.setFieldValue('projectDescription', state.autoSubmitPrompt);

      // Wait a bit for the field to be set, then submit
      setTimeout(() => {
        if (formikRef.current) {
          console.log('[Generate] Triggering form submission...');
          formikRef.current.handleSubmit();
        }
      }, 500);

      // Clear the pending project from context and sessionStorage
      clearPendingProject();

      // Clear the location state to prevent re-submission on page refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, autoSubmitted, clearPendingProject, navigate]);

  return (
      <DashboardLayout>
        {/* Enhanced Error Alert */}
        {error && (
            <div className='max-w-7xl mx-5'>
              <div className="relative overflow-hidden bg-gradient-to-br from-red-50 to-rose-50 border-2 border-red-200 rounded-xl p-4 shadow-lg mb-4">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-red-200/30 to-transparent rounded-full -translate-y-10 translate-x-10"></div>

                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-red-500 rounded-full">
                      <AlertCircle className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="font-bold text-red-800 text-lg">Error</h3>
                  </div>
                  <div className="text-red-700 text-sm leading-relaxed">
                    {error}
                  </div>
                </div>
              </div>
            </div>
        )}

        <Card
            borderColor="[#dbe0f6]"
            bgColor="white"
            minHeight="full"
            grow
            stretch
            maxWidth="max-w-7xl"
            mAuto
        >
          {/* Enhanced Validation Loading Overlay */}
          {validating && (
              <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center flex-col z-50">
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-purple-500/20 rounded-full blur-xl animate-pulse"></div>
                  <div className="absolute top-3/4 right-1/4 w-40 h-40 bg-blue-500/20 rounded-full blur-xl animate-pulse delay-1000"></div>
                  <div className="absolute bottom-1/4 left-3/4 w-36 h-36 bg-pink-500/20 rounded-full blur-xl animate-pulse delay-500"></div>
                </div>

                {/* Main Content */}
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 max-w-md mx-4 text-center border border-white/20">
                  {/* Spinning Icon with Glow */}
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full blur-lg opacity-60 animate-pulse"></div>
                    <div className="relative bg-gradient-to-r from-purple-600 to-blue-600 rounded-full p-4 mx-auto w-fit">
                      <Brain className="w-12 h-12 text-white animate-spin" />
                    </div>
                  </div>

                  {/* Text Content */}
                  <h3 className="text-white text-2xl font-bold mb-2 bg-gradient-to-r from-purple-200 to-blue-200 bg-clip-text text-transparent">
                    AI is Validating Your Prompt
                  </h3>
                  <p className="text-white/80 text-lg mb-1">Analyzing and optimizing...</p>
                  <p className="text-white/60 font-medium">Can take up to 1 minute</p>

                  {/* Progress Animation */}
                  <div className="mt-6 w-full bg-white/20 rounded-full h-2 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-500 to-blue-500 rounded-full animate-pulse"></div>
                  </div>
                </div>
              </div>
          )}

          <div className="gap-5 w-full grow p-1 xl:p-3 flex flex-col items-center overscroll-contain lg:px-0 px-2">
            <div className="w-[100%] sm:w-[95%] lg:w-[85%] xl:w-[80%] pt-3">
              <Stepper
                  steps={["Project Info", "Specs", "Generate and Refine"]}
                  step={3}
              />
            </div>

            <div className="w-[100%] sm:w-[95%] lg:w-[85%] xl:w-[80%] grow">
              {/* Enhanced Page Title */}
              <div className="mb-6">
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent mb-2 flex items-center gap-3">
                  <Brain className="w-8 h-8 text-purple-600" />
                  Project Description
                </h1>
                <p className="text-gray-600 text-lg">Let AI understand your vision and generate perfect specifications</p>
              </div>

              <div className="w-full min-w-[300px] mb-6">
                {/* Enhanced Success Info Alert */}
                {!validationError && !improvedPrompt && (
                    <div className="relative overflow-hidden bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-200 rounded-xl p-6 shadow-lg mb-6">
                      <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-blue-200/30 to-transparent rounded-full -translate-y-10 translate-x-10"></div>

                      <div className="relative z-10">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="p-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full">
                            <Sparkles className="h-5 w-5 text-white" />
                          </div>
                          <h3 className="font-bold text-blue-800 text-xl">AI-Powered Specification Generation</h3>
                        </div>
                        <div className="text-blue-700 leading-relaxed space-y-3">
                          <p className="text-base font-medium">
                            Write the most accurate description of your project. Our AI will analyze your requirements and generate comprehensive specifications.
                          </p>

                          <div className="bg-blue-50 rounded-lg p-4 border-l-4 border-blue-400">
                            <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                              <Sparkles className="h-4 w-4" />
                              Tips for the best results:
                            </h4>
                            <ul className="text-sm text-blue-700 space-y-1.5">
                              <li className="flex items-start gap-2">
                                <span className="text-blue-500 font-bold mt-0.5">•</span>
                                <span><strong>Describe your project features</strong> - Focus on what your software does, not how it works</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-500 font-bold mt-0.5">•</span>
                                <span><strong>Avoid technical details</strong> - Explain the functionality and user experience instead</span>
                              </li>
                              <li className="flex items-start gap-2">
                                <span className="text-blue-500 font-bold mt-0.5">•</span>
                                <span><strong>Be comprehensive</strong> - Include all features to get the most accurate specifications and architecture</span>
                              </li>
                            </ul>
                          </div>

                          <span className="font-semibold text-blue-800 bg-blue-100 px-3 py-1.5 rounded-lg inline-flex items-center gap-2">
                            <Wand2 className="h-4 w-4" />
                            You can later ask our AI to modify and refine the generated specifications.
                          </span>
                        </div>
                      </div>
                    </div>
                )}

                {/* Compact Validation Error Alert */}
                {validationError && (
                    <div className="relative overflow-hidden bg-gradient-to-br from-red-50 to-rose-50 border border-red-200 rounded-lg p-3 shadow-sm mb-4">
                      <div className="relative z-10">
                        <div className="flex items-center gap-2 mb-2">
                          <div className="p-1 bg-red-500 rounded-full">
                            <AlertCircle className="h-3 w-3 text-white" />
                          </div>
                          <h3 className="font-medium text-red-800 text-sm">Validation Feedback</h3>
                        </div>
                        <div className="text-red-700 text-xs leading-tight">
                          <div
                              className="space-y-1 [&>p]:mb-1 [&>div]:mb-1 [&>br]:hidden"
                              dangerouslySetInnerHTML={{__html: validationError}}
                          />
                        </div>
                      </div>
                    </div>
                )}

                {/* Compact Improved Prompt Alert */}
                {improvedPrompt && (
                    <div className="space-y-3">
                      <div className="relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 shadow-sm">
                        <div className="relative z-10">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="p-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full">
                              <Wand2 className="h-3 w-3 text-white" />
                            </div>
                            <h3 className="font-medium text-green-800 text-sm">AI Improved Your Prompt</h3>
                          </div>
                          <div className="text-green-700 bg-green-50 p-2 rounded border border-green-200">
                            <div
                                className="text-xs leading-tight prose-sm prose-green max-w-none [&>h1]:text-sm [&>h2]:text-sm [&>h3]:text-sm [&>h4]:text-sm [&>p]:text-xs [&>p]:mb-1 [&>ul]:text-xs [&>ol]:text-xs [&>li]:mb-0 prose-headings:text-green-800 prose-p:text-green-700 prose-ul:text-green-700 prose-ol:text-green-700 prose-strong:text-green-800 prose-a:text-green-600 hover:prose-a:text-green-800"
                                dangerouslySetInnerHTML={{__html: improvedPrompt}}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Neutral styled Use Prompt Button - aligned left */}
                      <div className="flex items-center gap-2">
                        {/* Enhanced Use Prompt Button Section */}
                        <div className="flex flex-col gap-3">
                          <div className="flex items-start gap-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                            <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mt-0.5">
                              <Sparkles className="h-4 w-4 text-white" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-blue-900 mb-1">
                                Ready to use this improved version?
                              </p>
                              <p className="text-xs text-blue-700 leading-relaxed">
                                This will replace your current description with the AI-optimized version below.
                              </p>
                            </div>
                          </div>

                          <button
                              onClick={handleUsePrompt}
                              className="group flex items-center justify-center gap-3 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold text-sm rounded-lg shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 border-0"
                          >
                            <div data-tour="copy-enhanced-prompt" className="flex items-center gap-2">
                              <CheckCircle className="h-4 w-4 group-hover:rotate-12 transition-transform duration-200" />
                              <span>Replace with AI-Improved Description</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs bg-white/20 px-2 py-1 rounded-full">
                              <span>↓</span>
                              <span>Form</span>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                )}
              </div>

              <Formik
                  innerRef={formikRef}
                  initialValues={{projectDescription: ""}}
                  validationSchema={validationSchema}
                  onSubmit={(values, {setSubmitting}) => {
                    onDescriptionValidation(values, setSubmitting)
                  }}
              >
                {({touched, errors, isSubmitting}) => (
                    <Form className="space-y-6">
                      <FormContainer>
                        <FormItem
                            label="Project Description"
                            labelClass="text-xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent"
                            invalid={
                              (errors.projectDescription && touched.projectDescription) as boolean
                            }
                            asterisk
                            htmlFor='shredDescription'
                            errorMessage={errors.projectDescription}
                        >
                          {/* Enhanced Editor Container */}
                          <div className="relative">
                            <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg blur opacity-20"></div>
                            <div className="relative bg-white rounded-lg border-2 border-gray-200 focus-within:border-purple-400 transition-colors">
                              <Ckeditor5Field name="projectDescription"/>
                            </div>
                          </div>
                        </FormItem>

                        {/* Enhanced Submit Button */}
                        <div className="flex justify-end my-6">
                          <AIButton
                              type="submit"
                              loading={isSubmitting}
                              variant="primary-cosmic"
                              size="md"
                              icon={<Send />}
                              showSparkles={true}
                              showArrow={true}
                              glowIntensity="medium"
                              disabled={isSubmitting}
                          >
                            Generate Specifications & Architecture
                          </AIButton>
                        </div>
                      </FormContainer>
                    </Form>
                )}
              </Formik>
            </div>
          </div>
        </Card>
      </DashboardLayout>
  );
};

export default Generate;